import React from 'react';
import { Space, Typography, Switch, Tooltip, Button } from 'antd';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import { Link } from 'react-router-dom';
import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_MARKETING_ADMIN,
  ROLE_MARKETING_MANAGER,
} from './roles';
import { EDIT_PRODUCT } from './routes';
import { hasAnyPermission, replaceMultiple } from '../utilities/helpers';

const { Text } = Typography;

export const catalogueTableColumns = ({
  onConfirmDelete,
  onViewDetails,
  updateProductStatus,
  hasEditPermission,
  hasDeletePermission,
}) => [
  {
    title: 'Product Type',
    key: 'is_sample',
    dataIndex: 'is_sample',
    render: (isSample) => <Text>{isSample ? 'Sample' : 'Retail'}</Text>,
  },
  {
    title: 'DHANDHA SKU',
    key: 'sku',
    dataIndex: 'dhandha_sku',
    render: (text, record) => (
      <Text
        className="text-theme-color"
        onClick={() => {
          onViewDetails(record);
        }}
        underline
      >
        {text}
      </Text>
    ),
  },
  {
    title: 'Name Of product',
    key: 'name',
    dataIndex: 'name',
    sorter: true,
  },
  {
    title: 'MRP/Unit',
    key: 'mrp',
    dataIndex: 'mrp',
    render: (val) => `₹ ${val}`,
    sorter: true,
  },
  {
    title: 'Main Category',
    key: 'main-category',
    dataIndex: ['ProductCategory', 'name'],
  },
  {
    title: 'Status',
    key: 'is_active',
    render: (record) => (
      <Switch
        checked={get(record, 'is_active')}
        disabled={!hasEditPermission}
        onChange={(checked) =>
          updateProductStatus(
            get(record, 'id'),
            checked,
            get(record, 'company_id')
          )
        }
      />
    ),
  },
  {
    title: 'Action',
    key: 'action',
    render: (record) => (
      <Space size="large">
        {hasDeletePermission && (
          <Tooltip title="Delete product">
            <DeleteTwoTone
              className="table-icon"
              twoToneColor="#009EA8"
              onClick={() => {
                onConfirmDelete(get(record, 'id'), get(record, 'company_id'));
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Edit product">
          <Link
            to={replaceMultiple(EDIT_PRODUCT, {
              ':companyId': get(record, 'company_id'),
              ':id': get(record, 'id'),
            })}
          >
            <EditTwoTone
              className="table-icon"
              twoToneColor="#009EA8"
              onClick={() => {}}
            />
          </Link>
        </Tooltip>
      </Space>
    ),
  },
];

export const productSelectionColumns = (
  showQuantiy,
  roleCode,
  selectedProducts,
  onChangeQuantity
) =>
  [
    {
      title: 'Product Type',
      key: 'product_type',
      dataIndex: 'is_sample',
      render: (text) => <Text>{text ? 'Sample' : 'Retail'}</Text>,
    },
    {
      title: 'Brand',
      key: 'brand',
      dataIndex: ['Company', 'name'],
      hidden: hasAnyPermission([ROLE_ADMIN, ROLE_MANAGER], [roleCode]),
    },
    {
      title: 'DHANDHA SKU',
      key: 'sku',
      dataIndex: 'dhandha_sku',
      render: (text) => (
        <Text className="text-theme-color" underline>
          {text}
        </Text>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      dataIndex: 'id',
      render: (id, record) => {
        const index = findIndex(selectedProducts, ['id', id]);

        if (!showQuantiy) return get(record, 'quantity');
        if (index === -1) return '--';
        const qunatity = get(selectedProducts, `${index}.quantity`);
        return (
          <Space size="large">
            <Button
              size="small"
              type="primary"
              shape="circle"
              icon={<MinusOutlined />}
              onClick={() => {
                onChangeQuantity(qunatity - 1 > 1 ? qunatity - 1 : 1, index);
              }}
            />
            <Text>{qunatity}</Text>
            <Button
              size="small"
              type="primary"
              shape="circle"
              onClick={() => {
                onChangeQuantity(qunatity + 1, index);
              }}
              icon={<PlusOutlined />}
            />
          </Space>
        );
      },
    },
    {
      title: 'Name Of product',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'MRP/Unit',
      key: 'mrp',
      dataIndex: 'mrp',
      render: (val) => `₹ ${val}`,
      sorter: true,
    },
    {
      title: 'Main Category',
      key: 'main-category',
      dataIndex: ['ProductCategory', 'name'],
    },
  ].filter((item) => !item.hidden);

export const ProductDescriptions = [
  {
    title: 'SKU Code',
    key: 'dhandha_sku',
  },
  {
    title: 'Product Name',
    key: 'name',
  },
  {
    title: 'Product Information',
    key: 'other_info',
  },
  {
    title: 'Brand',
    key: 'brand_name',
  },
  {
    title: 'Product Category',
    key: 'ProductCategory.name',
  },
  {
    title: 'Product SubCategory',
    key: 'ProductSubCategory.name',
  },
  {
    title: 'GS1 Code',
    key: 'gs1',
  },
  {
    title: 'MRP',
    key: 'mrp',
  },
  {
    title: 'GST Slab',
    key: 'GstSlab.name',
  },
  {
    title: 'GST-cess %',
    key: 'gst_cess',
  },
  {
    title: 'HSN code-cess',
    key: 'hsn_code',
  },

  {
    title: 'Packaging',
    key: 'Packaging.name',
  },
  {
    title: 'Shelf Life',
    key: 'ShelfLife.name',
  },
  {
    title: 'Weight/Vol',
    key: 'weight_vol',
  },
];

export const catalogueTableFiltersList = (categoryList) => [
  {
    type: 'Input',
    addonBefore: 'SKU',
    chipValueKey: 'dhandha_sku',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Input',
    subType: 'text',
    addonBefore: 'Name',
    chipValueKey: 'name',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Main Category',
    options: categoryList,
    optionLabelKey: 'name',
    optionValueKey: 'id',
    dropdownKey: 'showMainCategory',
    chipValueKey: 'category_id_1',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Status',
    options: [
      {
        value: true,
        label: 'Active',
      },
      {
        value: false,
        label: 'Inactive',
      },
    ],
    optionLabelKey: 'label',
    optionValueKey: 'value',
    dropdownKey: 'showStatus',
    chipValueKey: 'is_active',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Product Type',
    options: [
      {
        value: true,
        label: 'Sample',
      },
      {
        value: false,
        label: 'Retail',
      },
    ],
    optionLabelKey: 'label',
    optionValueKey: 'value',
    dropdownKey: 'showProductType',
    chipValueKey: 'is_sample',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
];

export const productSelectionTableFiltersList = (userComapnies) => [
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Brand',
    options: userComapnies,
    optionLabelKey: 'Company.name',
    optionValueKey: 'Company.id',
    dropdownKey: 'brand',
    chipValueKey: 'company_id',
    permissions: [ROLE_MARKETING_ADMIN, ROLE_MARKETING_MANAGER],
  },
];
